import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type Address } from 'viem';
import { faGift } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { GiftingAddressInput } from '@components/Gifting/GiftingAddressInput';
import { Button } from '@components/main-components/button/Button';
import { SquareImage } from '@components/main-components/image/BaseImage';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { ModalContext } from '@context/modal';
import { GiftingModalFragmentDoc } from '@graphql/reactQuery';
import { type FragmentType, getFragment, gql } from '@graphql/reactQuery';
import { useSelectReleaseNft } from '@hooks/useSelectReleaseNft';
import { useTransferToken } from '@hooks/useTransferToken';
import { EditionSelector } from './CommentV2/EditionSelector';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment GiftingModal on Release {
    id
    title
    contractAddress
    chainId
    coverImage {
      id
      url
      dominantColor
    }
    staticCoverImage {
      id
      url
      dominantColor
    }
    artist {
      id
      name
    }
  }
`);

export type GiftingModalProps = {
  releaseFrag: FragmentType<GiftingModalFragmentDoc>;
};

export const GiftingModal = ({ releaseFrag }: GiftingModalProps) => {
  const { closeModal } = ModalContext.useContainer();
  const [inputTouched, setInputTouched] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<string | null>(null);
  const release = getFragment(GiftingModalFragmentDoc, releaseFrag);
  const {
    title,
    coverImage,
    artist,
    contractAddress,
    chainId,
    id: releaseId,
    staticCoverImage,
  } = release;
  const onClose = () => closeModal();
  const [giftAddress, setGiftAddress] = React.useState<Address | null>(null);
  const {
    loadMore: loadMoreNfts,
    nftsOwnedByUser,
    selectedNft,
    selectedNftIndex,
    setSelectedNftIndex,
    isLoading: isLoadingNft,
  } = useSelectReleaseNft({
    releaseId,
  });
  const { transferToken, isTransferring, setIsTransferring } = useTransferToken({
    chainId,
    contractAddress: contractAddress as Address,
    releaseId,
    tokenId: selectedNft?.tokenId ?? '',
    transferToAddress: giftAddress ?? '0x',
  });
  const isLoading = isLoadingNft || isTransferring;
  const isDisabled = !giftAddress || !selectedNft?.tokenId;

  const onGift = async () => {
    setIsTransferring(true);
    await transferToken();
  };

  const body = (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3 border-b border-base200 pb-3">
        <div className="flex-shrink-0">
          <SquareImage
            alt={title}
            src={staticCoverImage?.url ?? coverImage.url}
            size={48}
            borderRadius={8}
          />
        </div>
        <div className="flex flex-col gap-1.5 truncate">
          <div className="truncate font-title text-title-xxs font-medium leading-[18px] text-base800">
            {title}
          </div>
          <div className="text-base-xs font-medium text-base500">{artist.name}</div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="text-base-m font-semibold text-black">Edition</div>
        <EditionSelector
          selectedIndex={selectedNftIndex}
          setSelectedIndex={setSelectedNftIndex}
          nfts={nftsOwnedByUser}
          loadMore={loadMoreNfts}
          showCommentedTag={false}
        />
      </div>
      <div className="flex flex-col gap-2">
        <div className="text-base-m font-semibold text-black">Wallet Address</div>
        <GiftingAddressInput
          eligibleQuantity={1}
          inputTouched={inputTouched}
          setInputTouched={setInputTouched}
          inputValue={inputValue}
          setInputValue={setInputValue}
          setGiftAddress={setGiftAddress}
          setOpen={null}
          inputDisabled={false}
        />
      </div>
      <div className="flex md:hidden">
        <Button
          variant="primary"
          label="Gift this song"
          fullWidth
          leadingIcon={<FontAwesomeIcon icon={faGift} />}
          size="XL"
          onClick={onGift}
          disabled={isDisabled}
          loading={isLoading}
        />
      </div>
    </div>
  );
  return (
    <StandardModal
      title="Gift this song"
      body={body}
      bodyWithoutPaddingBottom
      closeModal={onClose}
      callToAction={
        <Button
          variant="primary"
          label="Gift this song"
          fullWidth
          leadingIcon={<FontAwesomeIcon icon={faGift} />}
          size="XL"
          onClick={onGift}
          disabled={isDisabled}
          loading={isLoading}
          className="hidden md:flex"
        />
      }
      mobileFullScreen
    />
  );
};
